'use client';

import { updateCustomer } from '@eliadmin/core/src/api/customers/customers-api';
import { uploadFile } from '@eliadmin/core/src/api/general/general-api';
import { CreateCurriculumControls } from '@eliadmin/core/src/components/create-curriculum/create-curriculum-controls';
import { CreateCurriculumStepper } from '@eliadmin/core/src/components/create-curriculum/create-curriculum-stepper';
import { CreateCurriculumStep0 } from '@eliadmin/core/src/components/create-curriculum/steps/create-curriculum-step-0';
import { CreateCurriculumStep1 } from '@eliadmin/core/src/components/create-curriculum/steps/create-curriculum-step-1';
import { CreateCurriculumStep3 } from '@eliadmin/core/src/components/create-curriculum/steps/create-curriculum-step-3';
import { CreateCurriculumStep4 } from '@eliadmin/core/src/components/create-curriculum/steps/create-curriculum-step-4';
import { CreateCurriculumStep5 } from '@eliadmin/core/src/components/create-curriculum/steps/create-curriculum-step-5';
import { CreateCurriculumStep6 } from '@eliadmin/core/src/components/create-curriculum/steps/create-curriculum-step-6';
import { CreateCurriculumStep8 } from '@eliadmin/core/src/components/create-curriculum/steps/create-curriculum-step-8';
import { CreateCurriculumStepsBuckets } from '@eliadmin/core/src/components/create-curriculum/steps/create-curriculum-steps-validator';
import { transformCreateCurriculumData } from '@eliadmin/core/src/components/create-curriculum/transform-create-curriculum-data';
import { Container } from '@eliadmin/core/src/components/ui/container';
import { ResponsiveDialogProvider } from '@eliadmin/core/src/components/ui/responsive-dialog';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { useToast } from '@eliadmin/core/src/components/ui/use-toast';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useCreateCurriculumStepValidationReset } from '@eliadmin/core/src/hooks/jobportal/use-create-curriculum-step-validation';
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { initCustomerStore, useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { deepGet } from '@eliadmin/core/src/utils/misc-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useRouter } from 'next-nprogress-bar';
import Link from '@eliadmin/core/src/components/link';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { enabledSocials } from '@eliadmin/core/src/components/create-curriculum/create-curriculum-socials-utils';
import { CreateCurriculumStep7 } from '@eliadmin/core/src/components/create-curriculum/steps/create-curriculum-step-7';
import { isCurriculumCompleted } from '../../../utils/curriculum-utils';

export type CreateCurriculumSchema = z.infer<ReturnType<typeof createStudentProfileFormSchema>>;

const uploadFileHelper = async (file: any, locale: string, authToken: string) => {
  const formData = new FormData();
  formData.append('file', file);

  try {
    const res = await uploadFile({
      authToken,
      formData,
      locale,
    });
    return res.data;
  } catch (e: any) {
    return;
  }
};

const getDefaultValue = (key: string, me: any) => {
  const exists = me?.customer?.skills?.find((skill: any) => skill.name === key);

  if (exists) {
    if (key === 'certificate_description' || key === 'motivation_letter_description') {
      return exists.description;
    }
    return {
      ...exists,
      file_url: exists.file?.url ? exists.file.url : exists.file_url,
    };
  }
  return '';
};

export function CreateCurriculumInner() {
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);

  const { formValidationMessages } = useFormValidationMessages(t);
  useCreateCurriculumStepValidationReset();

  const { me, isInitialized } = useCustomerStore(s => s) as any;

  const form = useForm<z.infer<ReturnType<typeof createStudentProfileFormSchema>>>({
    mode: 'onChange',
    resolver: zodResolver(createStudentProfileFormSchema(formValidationMessages)),
    defaultValues: {
      categories:
        me?.customer?.categories?.map((category: any) => {
          return {
            label: category.name,
            value: category.id,
          };
        }) || [],
      city: me?.customer?.city || null,
      country: me?.customer?.country?.id || null,
      date_of_birth: me?.customer?.date_of_birth || null,
      position_name: me?.customer?.position_name || null,
      title_before: me?.customer?.title_before || '',
      title_after: me?.customer?.title_after || '',
      hasExtraInfo: !!me?.customer?.additional_information,
      extra_info: me?.customer?.additional_information || '',
      additional_information: me?.customer?.additional_information || '',
      hasPersonalSummary: !!me?.customer?.personal_summary,
      personal_summary: me?.customer?.personal_summary || '',
      video: me?.customer?.video || null,
      disciplines:
        me?.customer?.disciplines?.map((discipline: any) => {
          return {
            label: discipline.name,
            value: discipline.id,
          };
        }) || [],
      distance_to_work_kilometers: me?.customer?.distance_to_work_kilometers || null,
      driving_licences:
        me?.customer?.driving_licences?.map((driving_licence: any) => {
          return {
            label: driving_licence.name,
            id: driving_licence.id,
          };
        }) || [],
      hasDrivingLicence: me?.customer?.driving_licences?.length > 0,
      educations:
        me?.customer?.education?.map((education: any, i: number) => {
          return {
            ...education,
            id: i,
            country: education.country?.id,
            startYear: education.date_from
              ? new Date(education.date_from).getFullYear().toString()
              : null,
            endYear: education.date_to
              ? new Date(education.date_to).getFullYear().toString()
              : null,
          };
        }) || [],
      email: me?.customer?.email || '',
      employment_types:
        me?.customer?.employment_types?.map((employment_type: any) => {
          return {
            label: employment_type.name,
            value: employment_type.id,
          };
        }) || [],
      expected_salary: me?.customer?.expected_salary || null,
      experiences:
        me?.customer?.experiences?.map((experience: any) => {
          return {
            ...experience,
            startYear: experience.date_from
              ? new Date(experience.date_from).getFullYear().toString()
              : null,
            endYear: experience.date_to
              ? new Date(experience.date_to).getFullYear().toString()
              : null,
            startMonth: experience.date_from
              ? new Date(experience.date_from).getMonth().toString()
              : null,
            endMonth: experience.date_to
              ? new Date(experience.date_to).getMonth().toString()
              : null,
          };
        }) || [],
      gender: { id: me?.customer?.gender?.id?.toString() || '3' },
      id: me?.customer?.id || 14,
      languages:
        me?.customer?.languages?.map((lang: any) => {
          return {
            ...lang.language,
            id: lang.language.id,
            language: lang.language.name,
            level: lang.level,
          };
        }) || [],
      name: me?.customer?.name || '',
      phone: me?.customer?.phone || '',
      hasHobbies: !!me?.customer?.skills?.find((skill: any) => skill.type === 'hobby')?.description,
      hobbies:
        me?.customer?.skills?.find((skill: any) => skill.type === 'hobby')?.description || '',
      skills:
        me?.customer?.skills?.filter(
          (skill: any) =>
            !skill.type ||
            (skill.type != 'publication' &&
              skill.type != 'hobby' &&
              skill.type != 'honor' &&
              skill.type != 'project'),
        ) || [],
      skills_projects: me?.customer?.skills?.filter((skill: any) => skill.type === 'project') || [],
      motivation_letter_description: getDefaultValue('motivation_letter_description', me),
      video_file: getDefaultValue('video_file', me),
      certificate_description: getDefaultValue('certificate_description', me),
      curriculum_file: getDefaultValue('curriculum_file', me),
      image: me?.customer?.image || null,
      // photo_file: getDefaultValue('photo_file', me),
      start_date: me?.customer?.start_date || null,
      starting_date_immediately: me?.customer?.starting_date_immediately || false,
      surname: me?.customer?.surname || '',
      values: me?.customer?.values || [],
      working_hours:
        me?.customer?.working_hours?.map((working_hour: any) => {
          return {
            label: working_hour.name,
            value: working_hour.id,
          };
        }) || [],
      postcode: me?.customer?.postcode || null,
      street_number: me?.customer?.street_number || null,
      address: me?.customer?.address || null,
      socials: Object.fromEntries(
        enabledSocials.map(social => {
          const socialSkill = me?.customer?.skills?.find(
            (skill: any) => skill.type === 'honor' && skill.name === social,
          );
          return [
            social,
            {
              url: socialSkill?.description || '',
              name: socialSkill?.company_name || '',
            },
          ];
        }),
      ),
      show_in_index: me?.customer?.show_in_index ?? true,
    },
  });

  const { toast } = useToast();

  const handleSubmit = form.handleSubmit(async data => {
    setIsSubmitting(true);
    const transformedData = transformCreateCurriculumData(data);

    try {
      // Upload files if they exist and have changed
      const motivationLetterDescription = data.motivation_letter_description;
      const certificateDescription = data.certificate_description;
      // const photoFileUrl = await uploadIfChanged(data.image, 'image');

      // Update skills array with uploaded file URLs
      if (data.skills) {
        data.skills = data.skills.map((skill: any) => {
          if (skill.name === 'motivation_letter_description' && motivationLetterDescription) {
            skill.description = motivationLetterDescription;
          }
          if (skill.name === 'certificate_description' && certificateDescription) {
            skill.description = certificateDescription;
          }
          // if (skill.name === 'photo_file' && photoFileUrl) {
          //   skill.file_url = photoFileUrl;
          // }
          return skill;
        });

        if (data.skills_projects) {
          data.skills = [
            ...data.skills,

            ...data.skills_projects?.map((skill: any) => {
              return {
                ...skill,
                id: undefined,
              };
            }),
          ];
        }
      }

      // Upload and set image if it has changed
      if (data.image && form.formState.dirtyFields.image) {
        const imageUploadResponse = await uploadFileHelper(data.image, locale, me?.authToken);
        if (imageUploadResponse) {
          data.image = imageUploadResponse;
        }
      }

      // Submit the final transformed and updated data
      const response = (await updateCustomer({
        authToken: me?.authToken,
        data: transformedData as any,
        locale,
      })) as any;

      // Update customer store with the new data
      initCustomerStore({ authToken: me?.authToken, customer: response?.data });

      // Show success message and navigate to profile
      toast({
        title: t('text1783'), // Example translation key for success message
        variant: 'success',
        duration: 5000,
      });
      router.push(ROUTES.PROFILE());
    } catch (error: any) {
      if (error?.fields) {
        Object.keys(error.fields).forEach((field: any) => {
          const errorMessage = error.fields[field]?.[0] || 'An error occurred'; // Fallback error message
          form.setError(field, { type: 'manual', message: errorMessage });
        });
      }
      if (error?.fields?.city) {
        toast({
          title: t('text1472'), // Example translation key for error message
          description:
            'Kombinácia mesta a krajiny, ktorú ste zadali, neexistuje. Skontrolujte prosím zadané údaje.',
          variant: 'destructive',
        });
      } else {
        toast({
          title: t('text1472'), // Example translation key for error message
          description: 'Nastala chyba. Skontrolujte prosím zadané údaje.',
          variant: 'destructive',
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  });

  const router = useRouter();

  useEffect(() => {
    if (!me && isInitialized) {
      router.push(ROUTES.SIGN_IN());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me]);

  const [currentStep, setCurrentStep] = useState(0);
  const [currentErrorStep, setCurrentErrorStep] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const checkIfStepInputsValid = async (step: keyof typeof CreateCurriculumStepsBuckets) => {
    const currentBucket = CreateCurriculumStepsBuckets[step];

    if (!currentBucket) return true; // No fields to validate

    // Trigger validation only for fields in the current bucket
    const isValid = await form.trigger(currentBucket as any, { shouldFocus: true });

    // Check for errors (handle nested schemas with deepGet if needed)
    const hasErrors = currentBucket.some(field => {
      const error = deepGet(form.formState.errors, field);
      return !!error;
    });

    if (!isValid || hasErrors) {
      setCurrentErrorStep(step);
      return false;
    }
    setCurrentErrorStep(null);
    return true; // Step is valid
  };

  const handleSetCurrentStep = async (step: number) => {
    const isValid = await checkIfStepInputsValid(
      currentStep as keyof typeof CreateCurriculumStepsBuckets,
    );
    if (isValid) {
      setCurrentStep(step);
    }
  };

  return (
    <div className="w-[100vw]">
      <div className="flex-start left-0 mb-4 flex h-8 w-[100vw] items-center justify-center bg-white px-4 md:mb-6 md:h-10 md:px-2 lg:px-4">
        <p className="w-full max-w-[1171px] text-sm">
          <Link href={ROUTES.HOME()}>{t('text160')}</Link> / {t('text833')}
        </p>
      </div>
      <Container className={cn('flex flex-col gap-4 px-0 sm:px-4 md:gap-6', '')}>
        <h1 className="w-full whitespace-nowrap px-4 py-6 text-3xl font-bold md:py-8 md:text-5xl">
          {t('text833')}
        </h1>
        <div className="flex gap-4">
          <div className="hidden md:block">
            <CreateCurriculumStepper
              currentStep={currentStep}
              setCurrentStep={handleSetCurrentStep}
              currentErrorStep={currentErrorStep}
              isEditingExisting={isCurriculumCompleted(me?.customer)}
            />
          </div>
          <div className="flex w-full flex-col gap-4">
            <ResponsiveDialogProvider>
              <FormProvider {...form}>
                <form
                  className={cn(
                    'flex w-full items-start justify-start gap-2',
                    'ea-add-job-offer-form',
                  )}
                  // onSubmit={handleSubmit}
                >
                  {currentStep === 0 && <CreateCurriculumStep0 />}
                  {currentStep === 1 && <CreateCurriculumStep1 />}
                  {currentStep === 2 && <CreateCurriculumStep3 />}
                  {currentStep === 3 && <CreateCurriculumStep4 />}
                  {currentStep === 4 && <CreateCurriculumStep5 />}
                  {currentStep === 5 && <CreateCurriculumStep6 />}
                  {currentStep === 6 && <CreateCurriculumStep7 />}
                  {currentStep === 7 && <CreateCurriculumStep8 />}
                </form>
                <CreateCurriculumControls
                  currentStep={currentStep}
                  setCurrentStep={handleSetCurrentStep}
                  handleSubmit={handleSubmit}
                  isSubmitting={isSubmitting}
                />
              </FormProvider>
            </ResponsiveDialogProvider>
          </div>
        </div>
      </Container>
    </div>
  );
}

export const createStudentProfileFormSchema = (VM: FormValidationMessages) =>
  z.object({
    name: z.string().min(1, VM.REQUIRED_FIELD_VALIDATION_MESSAGE()),
    surname: z.string().min(1, VM.REQUIRED_FIELD_VALIDATION_MESSAGE()),
    date_of_birth: z.any(),
    categories: z.array(z.any()).nullable(),
    city: z.string().nullable(),
    country: z.string().nullable(),
    disciplines: z.array(z.any()).nullable(),
    distance_to_work_kilometers: z.number().nullable(),
    driving_licences: z.array(z.any()).nullable(),
    hasDrivingLicence: z.boolean().optional(),
    hasExtraInfo: z.boolean().optional(),
    hasHobbies: z.boolean().optional(),
    hobbies: z.string().optional(),
    extra_info: z.string().optional(),
    hasPersonalSummary: z.boolean().optional(),
    personal_summary: z.string().optional(),
    educations: z.array(z.any()).nullable(),
    email: z.string().email(),
    employment_types: z.array(z.any()).nullable(),
    expected_salary: z.number().nullable(),
    experiences: z.array(z.any()).nullable(),
    gender: z.any().nullable().optional(),
    id: z.number(),
    languages: z.array(z.any()).nullable(),
    phone: z.string(),
    skills: z.array(z.any()).nullable(),
    start_date: z.string().nullable(),
    starting_date_immediately: z.boolean(),
    values: z.array(z.any()).nullable(),
    working_hours: z.array(z.any()).nullable(),
    image: z.any().nullable(),
    certificate_description: z.any().nullable(),
    motivation_letter_description: z.any().nullable(),
    curriculum_file: z.any().nullable(),
    photo_file: z.any().nullable().optional(),
    video_file: z.any().nullable(),
    video: z.any().nullable(),
    position_name: z.string().nullable(),
    title_before: z.string().nullable(),
    title_after: z.string().nullable(),
    postcode: z.string().nullable(),
    street_number: z.string().nullable(),
    address: z.string().nullable(),
    socials: z.any().nullable(),
    additional_information: z.any().optional(),
    education: z.any().optional(),
    skills_projects: z.array(z.any()).nullable(),
    show_in_index: z.boolean().optional(),
  });

export default function CreateCurriculumWrapper() {
  const { me, isInitialized } = useCustomerStore(s => s) as any;

  if (!me && !isInitialized) {
    return (
      <div className="flex h-[200px] w-full items-center justify-center">
        <Spinner className="h-16 w-16 text-primary" />
      </div>
    );
  }

  if (isInitialized && me) {
    return <CreateCurriculumInner />;
  }

  return (
    <div className="flex h-[200px] w-full items-center justify-center">
      <Spinner className="h-16 w-16 text-primary" />
    </div>
  );
}
